import { z } from 'zod';
import { AutonomyClass, autonomyRank } from './actions.ts';
import { IsoDateTime } from './common.ts';
import { ResourceScope, scopeWithin } from './entitlement.ts';
import { PlatformError } from './errors.ts';

/**
 * 17a (ADR-015 §6, ADR-019): delegated authority. The **parent delegation is held by the server**: it is set on
 * the `ProcessInstance` when the instance starts (from the initiator's verified entitlements and the trigger's
 * authority, never from a caller payload) and on every spawned `Task`. A spawn request only *asks* for a
 * delegation; `narrowDelegation(persistedParent, requested, agentAuthority)` decides what the child gets.
 */

export const DelegationBudget = z
  .object({
    maxModelTurns: z.number().int().nonnegative().optional(),
    maxToolCalls: z.number().int().nonnegative().optional(),
    maxChildren: z.number().int().nonnegative().optional(),
    deadline: IsoDateTime.optional(),
  })
  .meta({ id: 'DelegationBudget' });
export type DelegationBudget = z.infer<typeof DelegationBudget>;

/**
 * Authority handed to an instance or task. Child authority = persisted parent delegation ∩ requested ∩
 * agent/tool authority ∩ policy (`narrowDelegation`); delegation never widens.
 *
 * Resource scope is never implicitly unbounded: an empty `resourceScope` is valid only with the explicit marker
 * `tenantWide: true`; a delegation that is not tenant-wide must fix at least one dimension.
 */
export const Delegation = z
  .object({
    /** Authenticated principal on whose behalf the process acts. */
    initiatorId: z.string().min(1),
    delegationRef: z.string().optional(),
    /** Action scopes (e.g. `erp:po:create`) the holder may request. */
    actionScopes: z.array(z.string().min(1)).default([]),
    /** Resources the authority covers (one value per dimension). Required; `{}` only with `tenantWide`. */
    resourceScope: ResourceScope,
    /** Explicit tenant-wide marker: covers every resource of the tenant. Only a tenant-wide parent grants it. */
    tenantWide: z.boolean().default(false),
    maxAutonomy: AutonomyClass,
    budget: DelegationBudget.prefault({}),
    /** 0 for authority started by a person or trigger; parent depth + 1 for a spawned task. */
    depth: z.number().int().nonnegative().default(0),
  })
  .superRefine((d, ctx) => {
    const dims = Object.keys(d.resourceScope).length;
    if (!d.tenantWide && dims === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['resourceScope'],
        message: 'a delegation that is not tenantWide must fix at least one scope dimension',
      });
    }
    if (d.tenantWide && dims > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['tenantWide'],
        message: 'a tenantWide delegation carries an empty resourceScope',
      });
    }
  })
  .meta({ id: 'Delegation' });
export type Delegation = z.infer<typeof Delegation>;
export type DelegationInput = z.input<typeof Delegation>;

/** What the selected agent and its tools may do at most (from the admitted agent definition and catalog). */
export interface AgentAuthority {
  actionScopes: readonly string[];
  autonomy: AutonomyClass;
  maxDelegationDepth: number;
}

/** True when the delegation covers a resource with `scope` (tenant-wide, or `scope` within the delegation's). */
export function delegationCovers(delegation: Delegation, scope: ResourceScope): boolean {
  return delegation.tenantWide || scopeWithin(scope, delegation.resourceScope);
}

function minDefined(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

function minDeadline(a: string | undefined, b: string | undefined): string | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Date.parse(a) <= Date.parse(b) ? a : b;
}

function widened(message: string, details: Record<string, unknown>): PlatformError {
  return new PlatformError('forbidden', message, { details: { reason: 'delegation_widened', ...details } });
}

/**
 * Child delegation = `parent` (the **persisted** delegation of the instance or parent task) ∩ `requested` ∩
 * `agent`. A request that claims more than the parent holds (an action scope, a tenant-wide or wider resource
 * scope, higher autonomy, another initiator) throws `forbidden` (`delegation_widened` / `initiator_changed`)
 * instead of being trimmed: a widening claim is a defect, not a negotiation. Agent authority trims silently.
 * Depth is parent + 1 and must not exceed the agent's `maxDelegationDepth` + 1 (`limit_exceeded`).
 */
export function narrowDelegation(
  parent: Delegation,
  requested: DelegationInput,
  agent: AgentAuthority,
): Delegation {
  const req = Delegation.parse(requested);
  if (req.initiatorId !== parent.initiatorId) {
    throw new PlatformError('forbidden', 'A child delegation keeps the initiator of its parent', {
      details: { reason: 'initiator_changed' },
    });
  }
  const extraScopes = req.actionScopes.filter((s) => !parent.actionScopes.includes(s));
  if (extraScopes.length > 0) {
    throw widened('Delegation requests action scopes the parent does not hold', {
      actionScopes: extraScopes,
    });
  }
  if (req.tenantWide && !parent.tenantWide) {
    throw widened('Delegation requests tenant-wide authority from a scoped parent', {
      parent: parent.resourceScope,
    });
  }
  if (!parent.tenantWide && !scopeWithin(req.resourceScope, parent.resourceScope)) {
    throw widened('Delegation requests a resource scope wider than the parent', {
      parent: parent.resourceScope,
      requested: req.resourceScope,
    });
  }
  if (autonomyRank(req.maxAutonomy) > autonomyRank(parent.maxAutonomy)) {
    throw widened('Delegation requests higher autonomy than the parent', {
      parent: parent.maxAutonomy,
      requested: req.maxAutonomy,
    });
  }
  const depth = parent.depth + 1;
  if (depth > agent.maxDelegationDepth + 1) {
    throw new PlatformError('limit_exceeded', 'Delegation depth exceeds the agent limit', {
      details: { depth, maxDelegationDepth: agent.maxDelegationDepth },
    });
  }
  const autonomy =
    autonomyRank(agent.autonomy) < autonomyRank(req.maxAutonomy) ? agent.autonomy : req.maxAutonomy;
  const deadline = minDeadline(parent.budget.deadline, req.budget.deadline);
  const maxModelTurns = minDefined(parent.budget.maxModelTurns, req.budget.maxModelTurns);
  const maxToolCalls = minDefined(parent.budget.maxToolCalls, req.budget.maxToolCalls);
  const maxChildren = minDefined(parent.budget.maxChildren, req.budget.maxChildren);
  const tenantWide = parent.tenantWide && req.tenantWide;
  return {
    initiatorId: parent.initiatorId,
    ...(req.delegationRef === undefined ? {} : { delegationRef: req.delegationRef }),
    actionScopes: req.actionScopes.filter((s) => agent.actionScopes.includes(s)),
    resourceScope: tenantWide ? {} : { ...parent.resourceScope, ...req.resourceScope },
    tenantWide,
    maxAutonomy: autonomy,
    budget: {
      ...(maxModelTurns === undefined ? {} : { maxModelTurns }),
      ...(maxToolCalls === undefined ? {} : { maxToolCalls }),
      ...(maxChildren === undefined ? {} : { maxChildren }),
      ...(deadline === undefined ? {} : { deadline }),
    },
    depth,
  };
}
